/**
 * Guard that decides whether a blog post view should be counted.
 *
 * What counts as a view: one view per blog post per page load, counted in the browser after
 * hydration. Deliberately NOT counted during SSR — SSR also runs for crawlers, link unfurlers
 * and route prefetches, so counting there would measure bots rather than readers.
 *
 * Per-mount-once is the simplest honest semantic and is what this gives, with one refinement:
 * because the claimed ids live in a module-level Set (not in a component ref), navigating away
 * from an article and back to it via client-side back/forward does not count a second view.
 * A full page reload starts a new module instance and so counts again. That is intentional —
 * this is a "reader opened the article" counter, not a unique-visitor metric. Deduplicating
 * per user/session would need server-side identity we deliberately do not collect here.
 *
 * The Set is only ever touched from a browser effect, so the SSR process never populates it
 * (which would otherwise leak state across requests). It grows only with the number of distinct
 * articles opened in a single page load.
 */
const claimedPostIds = new Set<string>();

/**
 * Atomically claims a view for `postId`: returns true only for the first call per post id, and
 * marks it as claimed in the same step.
 *
 * The test-and-set is one operation on purpose. React 19 StrictMode invokes effects twice on
 * mount, and a check-then-act guard would let both invocations pass before either marked the id.
 * JS runs this on a single thread, so no increment can interleave between has() and add().
 */
export const claimBlogView = (postId: string | null | undefined): boolean => {
  if (!postId) {
    return false;
  }

  if (claimedPostIds.has(postId)) {
    return false;
  }

  claimedPostIds.add(postId);

  return true;
};

/** Test seam: clears claimed ids so each test starts from a clean module state. */
export const resetBlogViewTracker = (): void => {
  claimedPostIds.clear();
};
